import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import MSG91OtpButton from "@/components/MSG91OtpButton";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ShieldCheck, Smartphone, Gift } from "lucide-react";

/**
 * Traveller signup — OTP-verified but with mandatory Name + Email + Phone
 * captured BEFORE the OTP flow starts (Iter26).
 * Backend: sends mode="signup" + signup_details so /auth/msg91/verify
 * enforces "already exists → 409" and creates a proper account (not a Guest xxxx).
 */
export default function RegisterTraveller() {
  const [searchParams] = useSearchParams();
  const initialRef = (searchParams.get("ref") || "").toUpperCase();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");

  if (initialRef && typeof window !== "undefined") {
    localStorage.setItem("swaan_pending_referral", initialRef);
  }

  const filled = name.trim().length >= 2 && /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim());

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-slate-50 py-10">
      <div className="max-w-5xl mx-auto px-4 md:px-8 grid lg:grid-cols-12 gap-8">
        <div className="lg:col-span-5">
          <div className="hw-label text-blue-700">For travellers · Sign up</div>
          <h1 className="hw-head text-4xl md:text-5xl font-black text-slate-900 mt-3 leading-tight">
            Join the marketplace.<br />Save on every stay.
          </h1>
          <p className="text-slate-600 mt-4 leading-relaxed">
            Fill your name, email, mobile — verify OTP — you&apos;re in. Same mobile logs you in every time after that.
          </p>
        </div>

        <div className="lg:col-span-7">
          <div data-testid="register-traveller-otp-card" className="hw-card p-7 hw-shadow-pop-amber space-y-4">
            <div className="hw-label text-blue-700">Sign up · Traveller</div>
            <h2 className="hw-head text-2xl md:text-3xl font-black text-slate-900">Create your account</h2>
            <p className="text-sm text-slate-600 leading-relaxed">
              Name, email and mobile are mandatory. We&apos;ll verify your mobile with an OTP.
            </p>

            <div>
              <Label className="hw-label">Full name *</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="reg-trav-name"
                className="rounded-sm mt-1" placeholder="How should the hotel address you?" autoComplete="name" />
            </div>
            <div>
              <Label className="hw-label">Email *</Label>
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value.trim())} data-testid="reg-trav-email"
                className="rounded-sm mt-1" placeholder="you@example.com" autoComplete="email" inputMode="email" />
              <p className="text-[11px] text-slate-500 mt-1">Bookings + invoices land here.</p>
            </div>

            <MSG91OtpButton
              intent="traveller"
              mode="signup"
              signupDetails={{ name: name.trim(), email: email.trim().toLowerCase() }}
              disabled={!filled}
              label={filled ? "Verify mobile OTP to sign up" : "Fill name & email first"}
            />

            {initialRef && (
              <div className="hw-card bg-emerald-50 border-emerald-200 p-3 flex items-start gap-2">
                <Gift className="w-4 h-4 text-emerald-700 mt-0.5 flex-shrink-0" />
                <div className="text-xs text-slate-700">
                  <span className="font-bold text-emerald-800">Referral applied — {initialRef}.</span> ₹100 to your wallet after signup.
                </div>
              </div>
            )}

            <div className="hw-card bg-slate-50 border-slate-200 p-3 flex items-start gap-3">
              <ShieldCheck className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
              <div className="text-xs text-slate-600 leading-relaxed">
                We only store your mobile as a login credential — no passwords. Hotels can text you booking confirmations.
              </div>
            </div>

            <div className="flex items-start gap-2 text-xs text-slate-500">
              <Smartphone className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <div>
                Already have an account? <Link to="/login" className="text-blue-700 hover:underline font-semibold">Log in with your mobile OTP</Link> — no need to sign up again.
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
